import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { fadeUp, viewport } from "@/lib/motion";
import PhoneMockup from "@/components/PhoneMockup";
import { useSiteConfig } from "@/context/SiteConfigContext";

const IMG_ROADSIDE = "https://images.unsplash.com/photo-1730514784243-f0e7f09c9f50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHx0b3clMjB0cnVjayUyMHJvYWRzaWRlJTIwYXNzaXN0YW5jZXxlbnwwfHx8fDE3ODYyMjc4OTJ8MA&ixlib=rb-4.1.0&q=85";

function Tag({ tag }) {
  if (!tag) return null;
  const c = tag === "AI" ? "bg-ai/10 text-ai" : "bg-brand/10 text-brand";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${c}`}>{tag}</span>;
}

export default function Features() {
  const { config } = useSiteConfig();
  const t = config.t.features;
  const FEATURES = config.lists.features;
  return (
    <section id="features" className="bg-white py-28 sm:py-36" data-testid="features-section">
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand">{t.eyebrow}</p>
          <h2 className="mt-4 max-w-3xl text-4xl font-bold tracking-tighter text-neutral-900 sm:text-5xl lg:text-6xl">
            {t.heading}
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-6">
          {/* big AI feature */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
            className="relative col-span-1 overflow-hidden rounded-3xl bg-gradient-to-br from-brand to-brand-dark p-8 text-white md:col-span-4 md:row-span-2">
            <Tag tag="AI" />
            <h3 className="mt-4 text-3xl font-bold tracking-tight">{FEATURES[0].title}</h3>
            <p className="mt-3 max-w-md text-white/80">{FEATURES[0].body}</p>
            <div className="mt-8 flex justify-center md:justify-start"><PhoneMockup screen="chat" className="scale-95" /></div>
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-ai/20 blur-3xl" />
          </motion.div>

          {/* nearby providers with image */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
            className="relative col-span-1 overflow-hidden rounded-3xl md:col-span-2 md:row-span-2">
            <img src={IMG_ROADSIDE} alt="Nearby providers" className="h-full min-h-[280px] w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-6 text-white">
              <Tag tag="Live" />
              <h3 className="mt-3 text-2xl font-bold tracking-tight">{FEATURES[1].title}</h3>
              <p className="mt-2 text-sm text-white/80">{FEATURES[1].body}</p>
            </div>
          </motion.div>

          {/* remaining */}
          {FEATURES.slice(2).map((f) => (
            <motion.div key={f.title} variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}
              className="col-span-1 rounded-3xl border border-black/5 bg-[#FAFAFC] p-7 transition-transform hover:-translate-y-1 md:col-span-3">
              <Tag tag={f.tag} />
              <h3 className="mt-3 text-xl font-bold tracking-tight text-neutral-900">{f.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-neutral-500">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
